/**
 * 描画コアに渡せる素材と、その受け入れ(#158)。
 *
 * 正本は [ADR-0030](../../../docs/adr/0030-source-kinds.md)。
 *
 * **素材は「動画」と「画像」の 2 種で、直和として受ける。**
 * 呼び出し側(ビューアシェル / S04)は**素材を作って渡すだけ**でよく、
 * 種類を意識する必要がない。
 *
 * ## なぜ描画コアに置くか
 *
 * **ビューアと S04 の両方が使う。** 種類の判定と自然サイズの取り方を
 * 呼び出し側それぞれに持たせると、**同じ規則が 2 か所に生まれる。**
 *
 * `packages/shared` には置けない —— あちらは API(Node)からも読まれ、
 * **DOM の型を持たない。**
 *
 * **素材はいっさい永続化しない**(設計書 9.5)。`File` /
 * `URL.createObjectURL` の範囲にとどめ、**送信のためのコードを持たない**
 * ことで担保する —— 表示上の注意書きは補助にすぎない。
 * このモジュールは `fetch` / `XHR` / `WebSocket` を一切使わない。
 */
/**
 * 素材が動画か。
 *
 * **`instanceof` を使わない。** 別レルム(iframe)の要素で false になる ——
 * 埋め込みビューアは Shadow DOM の中で動くので、素材が別の文書から
 * 来る経路がありうる。
 */
export function isVideoSource(source) {
    // **大文字に寄せる。** XHTML(`application/xhtml+xml`)で配信されたページでは
    // `tagName` が小文字になる —— 埋め込みビューアは第三者のページで動く。
    return source.tagName.toUpperCase() === 'VIDEO';
}
/**
 * 素材の自然サイズ。**動画と画像で取り方が違う**のをここで吸収する。
 *
 * 揃っていなければ 0 が返る(メタデータ待ちの `<video>` / 読み込み前の `<img>`)。
 * **呼び出し側は寸法 0 を見て描画を諦める** —— [ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md) の
 * `contain` は自然サイズから帯を決めるので、0 のまま描くと何も出ない。
 */
export function naturalSizeOf(source) {
    return isVideoSource(source)
        ? { width: source.videoWidth, height: source.videoHeight }
        : { width: source.naturalWidth, height: source.naturalHeight };
}
/**
 * この素材を GPU へ転送するか(#158 / **#168 で新しいフレームの判定を足した**)。
 *
 * **画像は 1 度だけ、動画は新しいフレームが来たときだけ**
 * ([ADR-0030](../../../docs/adr/0030-source-kinds.md) の論点 3 を
 * [ADR-0031](../../../docs/adr/0031-source-keyed-upload.md) が見直した)。
 *
 * 以前は動画に無条件で `true` を返していた。**描画ループは 60Hz だが動画は
 * 30fps のことが多く、半分は前と同じ画素を上げていた** —— 1080p は 1 回 8.29MB
 * なので、4 本で毎秒 2GB のバスを使う。これは GPU 使用率にもメモリ量にも
 * 現れないまま描画を落とす。
 *
 * ## 分からないときは上げる
 *
 * `currentToken` は「いま出ているフレームの番号」で、**`requestVideoFrameCallback`
 * が無い環境(Firefox)や、まだ 1 度も提示されていない間は `null`**。
 *
 * **`null` を「変わっていない」と解釈すると動画が固まる。** 分からないときは
 * 毎フレーム上げる —— 以前の挙動にそのまま落ちる。
 *
 * **純関数として切り出してある。** [ADR-0019](../../../docs/adr/0019-dev-foundation.md) により
 * WebGL は jsdom で試さないので、**判断がシェーダの呼び出しに埋まっていると
 * 検査できない** —— ここを誤ると「動画が止まる」「前の画像が出続ける」という
 * 最も痛い壊れ方をする。
 *
 * **読み込み途中の `<img>` は上げない。** 中途半端な絵が 1 度上がると、
 * 画像は再転送しないので**二度と直らない。** 動画の「寸法 0 なら描かない」と対称。
 */
export function shouldUpload(entry, 
/** いま描いている描画フレームの通し番号。 */
frame) {
    /*
     * **1 つの描画フレームで同じ素材を 2 回上げない。**
     *
     * 同じ素材が複数のセルに載っているとき(#152 の分割 / 同じファイルの共有)
     * セルごとに上げると**素材単位でテクスチャを持つ意味が消える。**
     *
     * フレーム番号が分からない環境では `currentToken` が常に `null` になり、
     * 下の判定だけでは毎セル上げてしまう —— **ここで止める。**
     */
    if (entry.uploadedFrame === frame)
        return false;
    return hasNewContent(entry);
}
/**
 * この素材に**まだ画面に出していない中身**があるか(#174)。
 *
 * [`shouldUpload`](#shouldUpload) の中身を切り出したもの。
 *
 * ## なぜ切り出すか
 *
 * **「このフレームを描く必要があるか」の判定と規則を共有するため**(#174)。
 * 変化が無いフレームを描かないようにしたが、**「変化が無い」の定義が
 * 転送の判定とずれると、描かないまま中身が変わる**(= 画面が固まる)。
 * **1 つの関数を両方から呼ぶ**ことで、ずれる余地をなくす。
 */
export function hasNewContent(entry) {
    /*
     * **中身が無いものは上げない**(#170)。
     *
     * 動画は**メタデータだけ届いた段階**(`readyState 1`)でも寸法を返すので、
     * 寸法を見ただけでは「貼れる」と判断できない。この状態で `texImage2D` を
     * 呼ぶと `INVALID_VALUE` で**テクスチャが確保されない。**
     */
    if (!hasFrameData(entry.source))
        return false;
    if (isVideoSource(entry.source)) {
        // 1 度も上げていないなら必ず上げる(でないと最初の絵が出ない)。
        if (entry.uploadedToken === undefined)
            return true;
        // **番号を信用していない** → 以前の挙動(毎フレーム)に落とす。
        if (!entry.trustToken)
            return true;
        // 番号が分からない → 同じく毎フレーム。
        if (entry.currentToken === null)
            return true;
        return entry.currentToken !== entry.uploadedToken;
    }
    return entry.uploadedToken === undefined;
}
/**
 * GPU へ転送できる中身があるか(#170)。
 *
 * ## なぜ寸法だけでは足りないか
 *
 * **動画はメタデータが届いた時点で寸法を返す**(`readyState 1` =
 * `HAVE_METADATA`)が、**まだ 1 枚も復号されていない。**
 * 受け入れの待ち([`waitForSourceReady`](#waitForSourceReady))は #341 から 1 枚目まで待つが、
 * **上限を超えると寸法だけで受け入れる**(再生するまで中身を読まない環境)。また寸法の後に
 * シークや作り直しで 1 枚目が消えることもある —— **割り当て直後の描画がこの状態を通る経路は残る。**
 *
 * この状態で `texImage2D` を呼ぶと **`INVALID_VALUE` になり、テクスチャは
 * 確保されない。** それを確保できたものとして扱うと、以後の `texSubImage2D`
 * は **`INVALID_OPERATION` で永久に書けない** —— **動画が真っ暗になる**(#170)。
 *
 * `HAVE_CURRENT_DATA`(2)は「いまの位置の絵がある」ことを意味する。
 * **定数を直に書く** —— グローバルを参照すると DOM の無い環境(テスト)で
 * 読み込めない。
 *
 * 画像は `complete` だけでは足りない —— **読み込みに失敗した `<img>` も
 * `complete` になる。** 幅まで見る。
 */
export function hasFrameData(source) {
    if (isVideoSource(source))
        return source.readyState >= 2;
    return source.complete && source.naturalWidth > 0;
}
/**
 * 番号が動かないまま**描画した**フレーム数がこれを超えたら、番号を信用しない(#168)。
 *
 * 60Hz なら約 1 秒。**固まったキャンバスが 1 秒以内に戻る**ようにする。
 *
 * **秒ではなく描画したフレーム数で数えるのが要点。** 秒(`currentTime` の差)で
 * 測ると、**描いていない間に進んだ時間まで数えてしまう** ——
 * タブを 1 秒離れるだけで必ず引っかかる(背景タブでは
 * `requestAnimationFrame` が止まる一方、動画の再生は続く)。
 *
 * 誤検知しても**毎フレーム転送に落ちるだけ**(#168 以前の挙動)で、
 * 壊れる方向ではない。**1fps を下回る素材では誤検知する。**
 */
export const TOKEN_STALE_FRAMES = 60;
/** 番号を素直に信用している状態。 */
export const TRUSTED_TOKEN = { trustToken: true, staleSinceFrame: null };
/**
 * 提示フレームの番号を信用し続けてよいかを決める(#168)。
 *
 * ## なぜ要るか
 *
 * `requestVideoFrameCallback` は **「無い」だけでなく「あるが発火しなくなる」**
 * ことがある —— 提示フレームが来なければ呼ばれない仕様なので、
 * **供給元が合成の対象から外れれば番号は止まる。**
 * 供給元は意図的に見えなくしてあるので、この経路は現実にありうる。
 *
 * [`shouldUpload`](#shouldUpload) は「番号が同じなら上げない」ので、
 * **番号が止まると転送も止まる** —— 動画は再生され続け、操作 UI も
 * 「再生中」のまま、**キャンバスだけが固まる。** これは最も痛い壊れ方である。
 *
 * ## 何を見るか
 *
 * **番号が動かないまま「描いた」フレーム数**を見る
 * ([`TOKEN_STALE_FRAMES`](#TOKEN_STALE_FRAMES))。
 *
 * | 測り方 | だめな理由 |
 * |---|---|
 * | `currentTime` の差(秒) | **描いていない間に進んだ分まで数える。** タブを離れるだけで引っかかる |
 * | 見送った回数を数える | **同じ素材が複数のセルに載るとセルの数だけ増える。** 閾値の意味が構成で変わる |
 *
 * 通し番号の**差**を見るので、セルがいくつあっても、また描画が止まっていても
 * 正しく数えられる。
 *
 * ## 戻せるようにする(双方向)
 *
 * **番号が再び動き出したら信用を戻す。** 片道にすると、一度の誤検知で
 * **その素材の最適化が永久に失われる** —— タブを 1 回切り替えただけで
 * そうなるのでは割に合わない。
 */
export function nextTokenTrust(entry, 
/** いま描いている描画フレームの通し番号。 */
frame) {
    // 画像に番号の概念がない。**常に信用する**(そもそも 1 度しか上げない)。
    if (!isVideoSource(entry.source))
        return TRUSTED_TOKEN;
    // まだ 1 度も上げていない / 番号が動いた → **信用を戻す。**
    if (entry.uploadedToken === undefined)
        return TRUSTED_TOKEN;
    if (entry.currentToken !== entry.uploadedToken)
        return TRUSTED_TOKEN;
    // **止まっている動画は番号も止まって当然。** これで落としてはいけない。
    if (entry.source.paused)
        return TRUSTED_TOKEN;
    const since = entry.staleSinceFrame ?? frame;
    return { trustToken: frame - since < TOKEN_STALE_FRAMES, staleSinceFrame: since };
}
/**
 * テクスチャの格納域を確保し直す必要があるか(#168)。
 *
 * **1 度目は `texImage2D`、以後は `texSubImage2D`。** 前者は毎回テクスチャの
 * 形を作り直すが、後者は**既にある格納域に書き込むだけ**で済む。
 *
 * **`texStorage2D` は使わない**([ADR-0031](../../../docs/adr/0031-source-keyed-upload.md))——
 * サイズを固定するため、寸法が変わるとテクスチャ自体を作り直すことになる。
 * `texImage2D` は何度でも呼べるので、**寸法が変わった回だけ**呼べば同じ効果になる。
 *
 * 寸法が変わる経路は 2 つ ——
 * **素材の差し替え**(別の動画に替える)と、**動画のメタデータが後から届く**
 * (最初は 0×0 で、`loadedmetadata` で本当の寸法になる)。
 */
export function needsRealloc(allocated, size) {
    if (allocated === null)
        return true;
    return allocated.width !== size.width || allocated.height !== size.height;
}
/** 提示フレームを数えられない素材のための、何もしない追跡。 */
const NO_TRACKING = {
    token: () => null,
    stop: () => {
        /* 何もしない */
    },
};
/**
 * 提示フレームの追跡を始める(#168)。
 *
 * 動画でなければ、また `requestVideoFrameCallback` が無ければ
 * **何もしない追跡**を返す(`token()` は常に `null`)。
 */
export function trackPresentedFrames(source) {
    if (!isVideoSource(source))
        return NO_TRACKING;
    /*
     * **機能検出は実行時に行う。**
     *
     * `lib.dom` は `requestVideoFrameCallback` を**必須**として型付けしているが、
     * **実体が無い環境がある**(Firefox)。型を信じて呼ぶと落ちる。
     *
     * 型のほうを省略可に上書きすると、こんどは `?.` が
     * 「不要な省略」として lint に叱られる —— **判定だけ実行時に寄せる。**
     */
    if (!hasFrameCallback(source))
        return NO_TRACKING;
    let presented = null;
    let handle = null;
    let stopped = false;
    const onFrame = (_now, meta) => {
        presented = meta.presentedFrames;
        // **毎回張り直す。** 1 回きりの予約なので、続けるには再登録が要る。
        // **`stopped` を見る** —— 破棄したあとに 1 発届いて予約が復活するのを防ぐ。
        if (!stopped)
            handle = source.requestVideoFrameCallback(onFrame);
    };
    handle = source.requestVideoFrameCallback(onFrame);
    return {
        token: () => presented,
        stop: () => {
            stopped = true;
            if (handle !== null)
                source.cancelVideoFrameCallback(handle);
            handle = null;
        },
    };
}
/** `requestVideoFrameCallback` の実体があるか。**型ではなく実行時に見る。** */
function hasFrameCallback(video) {
    const probe = video;
    return typeof probe.requestVideoFrameCallback === 'function';
}
/**
 * 素材が GPU の扱える大きさを超えていないか(#158)。
 *
 * **超えた素材は貼れない。** `texImage2D` が失敗し、**セルが空のまま**になる ——
 * 「入れたのに出ない」という最も分かりにくい壊れ方なので、
 * 呼び出し側が**理由を出して弾く**ために使う。
 *
 * `limit` は [`Renderer.maxSourceSize`](./types.ts) から取る。
 * **0(分からない)なら判定しない** —— フォールバック描画には GL が無い。
 */
export function exceedsTextureLimit(size, limit) {
    if (limit <= 0)
        return false;
    return size.width > limit || size.height > limit;
}
/** 拡張子での判定(MIME が空のケースがある — #33)。 */
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'ogv', 'ogg', 'mkv'];
/**
 * 画像の拡張子。
 *
 * **`svg` は入れない**([ADR-0030](../../../docs/adr/0030-source-kinds.md) の論点 5)。
 * 自然サイズを持たない SVG があり、`contain` が計算できずに**黙って何も出ない。**
 *
 * **`gif` は入れる**が、[ADR-0030](../../../docs/adr/0030-source-kinds.md) の論点 4 により
 * **最初のフレームで止まる**(アニメーションは再生しない)。
 */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'avif', 'bmp', 'gif'];
/**
 * 受け入れる素材の種類。**どちらでもなければ `null`。**
 *
 * **MIME と拡張子の両方で見る。** OS やブラウザによって `type` が空に
 * なることがあり、MIME だけに頼ると正当なファイルを弾く(#33)。
 *
 * **SVG は MIME と拡張子の両方で弾く。** `image/*` の網に入ってしまうため、
 * 先に落とす。
 */
export function sourceKindOf(file) {
    const extension = extensionOf(file.name);
    // **SVG は最初に落とす**(`image/*` の網に入るため)。
    if (file.type === 'image/svg+xml' || extension === 'svg')
        return null;
    /*
     * **音声だと分かっているものは拡張子に進ませない。**
     *
     * `.ogg` は音声にも動画にも使われる。MIME が `audio/*` と言っている
     * ファイルを拡張子だけで動画に落とすと、**盤面に音声を割り当てて
     * 何も映らない**という分かりにくい壊れ方になる。
     */
    if (file.type.startsWith('audio/'))
        return null;
    if (file.type.startsWith('video/'))
        return 'video';
    if (file.type.startsWith('image/'))
        return 'image';
    // `type` が空、または `application/octet-stream` のときは拡張子で見る。
    if (VIDEO_EXTENSIONS.includes(extension))
        return 'video';
    if (IMAGE_EXTENSIONS.includes(extension))
        return 'image';
    return null;
}
/** 小文字の拡張子(ドットなし)。拡張子がなければ空文字。 */
export function extensionOf(fileName) {
    const dot = fileName.lastIndexOf('.');
    if (dot < 0 || dot === fileName.length - 1)
        return '';
    return fileName.slice(dot + 1).toLowerCase();
}
/**
 * 素材の要素を作る。
 *
 * **`playsinline muted loop` は動画のときだけ**(#33 のモバイル対処)。
 * `playsinline` がないと iOS で全画面に飛び、`muted` がないと自動再生
 * ポリシーで再生が始まらない。**オプションにしない**(設計書 7.5 で既定)。
 *
 * **要素は Renderer の外で作る。** PoC 3.5 の未解決問題(60fps 動画で
 * 再生が実時間から遅れる。テクスチャ供給元の配置に起因する疑い)を
 * 切り分けられるよう、配置を呼び出し側が変えられる形にしておく。
 */
export function createRendererSource(file, kind) {
    const element = kind === 'video' ? createVideo() : document.createElement('img');
    element.src = URL.createObjectURL(file);
    element.setAttribute('style', HIDDEN_SOURCE_STYLE);
    return element;
}
/** **呼び出し側が持つ URL** で作った要素(`createRendererSourceFromUrl`)。破棄しても URL を解放しない。 */
const borrowedUrls = new WeakSet();
/**
 * **URL から**素材の要素を作る(#284 / [ADR-0048](../../../docs/adr/0048-embed-assign-api.md))。
 *
 * 埋め込み先が配信元の URL を渡す構成のため。素材を一度メモリへ載せずに済む(動画は Range で取る)。
 *
 * **`crossOrigin = 'anonymous'` を `src` より先に付ける。** 後から付けると、CORS なしで読み始めた
 * 要素になり、**WebGL に転送できない**(テクスチャが汚れて `texImage2D` が失敗する)。
 * 配信元に CORS(206 の応答を含む)が無ければ読み込みが失敗する —— **描けないまま「入った」ことにならない。**
 *
 * **別オリジンには Cookie を送らない**(`anonymous` —— 同じオリジンの URL では送る)。
 *
 * **渡された URL は解放しない**([`destroyRendererSource`](#destroyRendererSource))—— `blob:` の URL を
 * 渡されることがあり、それは**呼び出し側の持ち物**(勝手に `revokeObjectURL` すると、埋め込み先の
 * ページで同じ URL を使っている箇所まで壊れる —— #284 の最終レビュー)。
 */
export function createRendererSourceFromUrl(url, kind) {
    const element = kind === 'video' ? createVideo() : document.createElement('img');
    borrowedUrls.add(element);
    element.crossOrigin = 'anonymous';
    element.src = url;
    element.setAttribute('style', HIDDEN_SOURCE_STYLE);
    return element;
}
/**
 * 供給元の要素の隠し方(#168 / PoC 3.5 の申し送り 1)。
 *
 * ## 3 つの制約を同時に満たす必要がある
 *
 * | 制約 | 理由 |
 * |---|---|
 * | **`display: none` にしない** | 復号が止まる実装がある |
 * | **画面に見えない** | 供給元は素材の中身を見せる場所ではない |
 * | **面積を潰さない** | ← **#168 で足した。下記** |
 *
 * ## なぜ 1px をやめたか
 *
 * 以前は `width: 1px; height: 1px` だった。**PoC 3.5 で「60fps の動画は
 * 描画が 60fps のまま再生が実時間から大きく遅れる」現象**が出ており
 * ([`FINDINGS.md`](../../../poc/d13-projection/FINDINGS.md))、申し送り 1 は
 * **「事実上不可視の動画の復号優先度が下げられている疑い」**を挙げていた。
 *
 * 利用者から「一部の動画だけ進みが遅い」(#168)という報告が来たため、
 * **面積を潰さない置き方に変える。** 16:9 の小さな箱に収め、
 * `opacity: 0` と `pointer-events: none` で見えなくする。
 *
 * ## これは推測である
 *
 * **効果は測れていない。** エージェントの環境ではページが合成されず、
 * `requestAnimationFrame` が動かないため再生の遅れを再現できない ——
 * PoC のときも計測は利用者の画面で行っている。
 *
 * **単独のコミットにしてある**ので、効かなければこの 1 か所を戻せる。
 */
const HIDDEN_SOURCE_STYLE = [
    /*
     * **並べる。重ねない**(#168)。
     *
     * 以前は `position: absolute; width: 1px; height: 1px` だった。
     * 絶対配置のまま面積を持たせると**全部が同じ位置に完全に重なり、
     * 1 本を除いて隠れる** —— 「不可視なら復号優先度が下がる」という仮説の
     * もとでは、**まさに「一部の動画だけ遅い」を作ってしまう。**
     *
     * 置き場を `flex-wrap` にして、ここは**流れに乗るだけ**にする。
     * 置き場: ビューアは `.signa-videos`、S04 は `.videoHost`(どちらも 160×90)。
     */
    'flex: 0 0 auto',
    'width: 40px',
    'height: 22px',
    // **不透明度は置き場も持っている。** ここにも書くのは、置き場の CSS を
    // 読み落とした人が「見えるのでは」と誤解するのを防ぐため。
    'opacity: 0',
    'pointer-events: none',
].join('; ');
function createVideo() {
    const video = document.createElement('video');
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = false; // 再生開始は呼び出し側が制御する(失敗を検知するため)。
    video.preload = 'auto';
    return video;
}
/**
 * 素材の要素を破棄する。
 *
 * **`revokeObjectURL` を必ず呼ぶ。** 呼ばないと Blob がタブを閉じるまで
 * 解放されない。差し替えを繰り返すと積み上がる。**画像でも同じ。**
 */
export function destroyRendererSource(source) {
    const url = source.src;
    if (isVideoSource(source)) {
        source.pause();
        source.removeAttribute('src');
        source.load(); // デコーダを解放する。
    }
    else {
        source.removeAttribute('src');
    }
    // **自分で作った `blob:` だけを解放する**(借りた URL は呼び出し側の持ち物)。
    if (url.startsWith('blob:') && !borrowedUrls.has(source))
        URL.revokeObjectURL(url);
    source.remove();
}
/**
 * `play()` が拒否された理由を分ける(#341)。
 *
 * ## なぜ分けるか
 *
 * 以前は拒否をすべて「自動再生の拒否」として扱い、「再生を押して」と案内していた。
 * **Edge は HEVC を受け入れた後で `NotSupportedError` で拒否する** —— 押しても直らない
 * のに押せと言っていた。
 *
 * ## 何を見るか
 *
 * **要素の `error`(復号の失敗)を先に見る。** あれば理由の名前が何であれ形式の問題 ——
 * 失敗の後の `play()` は `NotSupportedError` で拒否されるが、名前だけに頼らない。
 * 次に理由の名前が **`NotSupportedError`** なら形式の問題。**それ以外は `blocked`**
 * (`NotAllowedError` のほか、`pause()` の割り込みの `AbortError` も —— 扱いは呼び出し側の今のまま)。
 *
 * **`instanceof DOMException` を使わない。** 別レルム(iframe)の例外で false になる
 * ([`isVideoSource`](#isVideoSource) と同じ理由)。名前だけを見る。
 */
export function playFailureOf(video, reason) {
    if (video.error !== null)
        return 'unsupported';
    return errorNameOf(reason) === 'NotSupportedError' ? 'unsupported' : 'blocked';
}
/** 例外の `name`。**名前を持たない値なら空文字。** */
function errorNameOf(reason) {
    if (typeof reason !== 'object' || reason === null || !('name' in reason))
        return '';
    return typeof reason.name === 'string' ? reason.name : '';
}
/**
 * 再生を試みる。**動画でなければ何もせず `played`。**
 *
 * **失敗の理由を返す**([`PlayOutcome`](#PlayOutcome))。ミュートでも自動再生が拒否される
 * 環境があるため、`blocked` なら呼び出し側は「再生」を押してもらう(#33 のモバイル対処)。
 * `unsupported` なら押しても直らない(#341)。
 */
export async function tryPlay(source) {
    if (!isVideoSource(source))
        return 'played';
    /*
     * **既に失敗している要素は `play()` を呼ばない**(#341 の最終レビュー)。
     *
     * `play()` がすぐ拒否するのは `MEDIA_ERR_SRC_NOT_SUPPORTED` のときだけで、
     * 復号の失敗(`MEDIA_ERR_DECODE`)では**約束が決着しないことがある** —— 待つ側
     * (`playAll` の `Promise.all`)が止まり、他の動画の案内まで出なくなる。
     */
    if (source.error !== null)
        return 'unsupported';
    try {
        await source.play();
        return 'played';
    }
    catch (reason) {
        return playFailureOf(source, reason);
    }
}
/**
 * まとめて再生した結果を 1 つにまとめる(#341)。**形式の問題を最優先する。**
 *
 * 1 本でも `unsupported` があれば `unsupported` —— 押せば直る自動再生の拒否より
 * **直し方が違う**(素材を作り直す)ので、そちらを伝える。次に `blocked`。
 * **空なら `played`**(失敗が無い)。
 *
 * ビューアの操作の帯と S04 の「再生」「同期」が**同じ規則**を使う。
 */
export function summarizePlayOutcomes(outcomes) {
    if (outcomes.includes('unsupported'))
        return 'unsupported';
    if (outcomes.includes('blocked'))
        return 'blocked';
    return 'played';
}
/**
 * 自然サイズが読めるまで待つ。読めなければ `false`。
 *
 * **サイズが要る。** [ADR-0028](../../../docs/adr/0028-video-fit-and-panel-aspect.md) の
 * `contain` は素材の自然サイズから帯を決めるので、**0 のまま描くと何も出ない。**
 *
 * 動画は `loadedmetadata`、画像は `load` を待つ。
 *
 * **素材の受け入れには使わない**(#341)—— 動画は寸法が読めても再生できるとは限らない。
 * 受け入れは [`waitForSourceReady`](#waitForSourceReady)(これを 1 段目に使う)。
 * **`index.ts` から公開しない**のは、呼び出し側がこちらを呼び続けられないようにするため。
 */
export function waitForNaturalSize(source) {
    if (isVideoSource(source)) {
        // `HTMLMediaElement.HAVE_METADATA` の値。**定数を直に書く** ——
        // グローバルを参照すると DOM の無い環境(テスト)で読み込めない。
        if (source.readyState >= 1)
            return Promise.resolve(true);
        /*
         * **`emptied` も失敗として扱う**(#168)。
         *
         * 共有した要素を待っている最中に、別の経路が
         * [`destroyRendererSource`](#destroyRendererSource) でその要素を捨てると
         * `load()` が走る。このとき飛ぶのは `error` ではなく **`emptied`** ——
         * 見ていないと**この待ちが永久に解決せず、ドロップが黙って消える。**
         */
        return once(source, 'loadedmetadata', ['emptied']);
    }
    // **`complete` だけでは足りない。** 読み込みに失敗した `<img>` も
    // `complete` になるので、幅が取れているかまで見る。
    if (source.complete)
        return Promise.resolve(source.naturalWidth > 0);
    return once(source, 'load', []);
}
/**
 * 動画の 1 枚目を待つ上限(#341)。**超えたら受け入れる。**
 *
 * **再生するまで中身を読まない環境がありうる**(iOS Safari が疑わしい —— `preload` を
 * 守らない)。そこで 1 枚目を待ち続けると**受け入れそのものが止まる** —— 待ちきれなければ
 * #341 以前のふるまい(寸法だけで受け入れる)に落とす。
 *
 * **デスクトップの通常の経路では当たらない。** 実測(2026-10-27、Edge 155 / Chrome 156):
 * H.264 4K の 1 枚目も、Edge の HEVC の失敗も `loadedmetadata` の直後に来た。
 */
export const FIRST_FRAME_GRACE_MS = 3000;
/**
 * 素材を受け入れてよいかが分かるまで待つ(#341)。**読めなければ `false`。**
 *
 * **動画は「寸法が読めた」だけでは受け入れない。** 続けて **1 枚目が復号できた**
 * (`loadeddata`)か、**失敗した**かまで待つ。
 *
 * ## なぜ寸法だけでは足りないか
 *
 * 再生できない形式でも、**寸法を読んでから失敗するブラウザがある。** Edge は HEVC の
 * 寸法を読んで `loadedmetadata` を出し、**その後で**復号器の初期化に失敗する
 * (`DECODER_ERROR_NOT_SUPPORTED`)。寸法で受け入れると、**映らない素材がセルに入り**、
 * `play()` の拒否を自動再生の拒否と取り違えて案内していた(#341)。
 * Chrome は同じ素材で `loadedmetadata` を出さずに失敗するので、寸法の段階で弾けていた。
 *
 * ## 上限を超えたら受け入れる
 *
 * [`FIRST_FRAME_GRACE_MS`](#FIRST_FRAME_GRACE_MS) を超えても 1 枚目が来なければ `true`。
 * **「読めなかった」にはしない** —— 中身を読まないだけの環境で、再生できる素材を弾いてしまう。
 *
 * **画像は [`waitForNaturalSize`](#waitForNaturalSize) と同じ**(読み込みと幅)。
 */
export async function waitForSourceReady(source, firstFrameGraceMs = FIRST_FRAME_GRACE_MS) {
    if (!(await waitForNaturalSize(source)))
        return false;
    if (!isVideoSource(source))
        return true;
    // **既に 1 枚目がある**(使い回した要素 —— #168)なら待たない。
    if (hasFrameData(source))
        return true;
    // **寸法の後で既に失敗している**なら待たない(`error` はもう来ない)。
    if (source.error !== null)
        return false;
    /*
     * **成功の合図は `loadeddata` だけではない**(#341 の最終レビュー)。
     *
     * 使い回した要素(#168)が**合流のシークの最中**だと `readyState` が 1 に下がるが、
     * `loadeddata` は読み込み 1 回につき 1 度しか来ない —— それだけを待つと**上限まで遅れる。**
     * シークが明ければ `seeked` / `canplay` が来る。どれも「絵がある」ことを意味する。
     *
     * **`emptied` も失敗として扱う**(#168)—— 1 段目と同じ理由。待っている間に
     * 共有元が捨てられると `error` ではなく `emptied` が来る。
     */
    return once(source, ['loadeddata', 'canplay', 'seeked'], ['emptied'], {
        ms: firstFrameGraceMs,
        result: true,
    });
}
/**
 * 成功のイベントか、失敗のイベントのどれかを待つ。
 *
 * **失敗の合図は複数ありうる。** `error` に加えて、動画では `emptied`
 * (途中で捨てられた)も失敗として扱う —— 1 つでも取りこぼすと
 * **待ちが永久に解決しない。**
 *
 * `timeout` を渡すと、**どれも来ないまま `ms` が過ぎたら `result` で解決する**(#341)。
 * どの終わり方でも**リスナとタイマーを両方外す。**
 */
function once(target, success, failures, timeout) {
    const successEvents = typeof success === 'string' ? [success] : success;
    const failureEvents = ['error', ...failures];
    return new Promise((resolve) => {
        let timer;
        const done = (ok) => {
            if (timer !== undefined)
                clearTimeout(timer);
            for (const name of successEvents)
                target.removeEventListener(name, onSuccess);
            for (const name of failureEvents)
                target.removeEventListener(name, onFailure);
            resolve(ok);
        };
        const onSuccess = () => {
            done(true);
        };
        const onFailure = () => {
            done(false);
        };
        for (const name of successEvents)
            target.addEventListener(name, onSuccess, { once: true });
        for (const name of failureEvents)
            target.addEventListener(name, onFailure, { once: true });
        if (timeout !== undefined) {
            timer = setTimeout(() => {
                done(timeout.result);
            }, timeout.ms);
        }
    });
}
//# sourceMappingURL=source.js.map